import { Box, useMediaQuery, Divider, IconButton } from '@mui/material'
import * as yup from 'yup'
import AddIcon from '@mui/icons-material/Add'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { TooltipText } from 'components/TooltipText/TooltipText'
import { SaveBtn } from 'components/SaveBtn/SaveBtn'
import { FieldArray, Form, Formik } from 'formik'
import { useCodeList } from 'hooks/useCodeList'
import { usePutMasterProduct } from 'hooks/useMasterProducts'
import { formatMultiLanguageField } from 'lib/utils'
import { t } from 'i18next'
import { contentLanguages } from 'lib/languages'
import theme from 'lib/theme'
import {
  Energy,
  MandatoryNutrientDetails,
  NutrientDetail,
} from 'types/model/product'
import { NutrientDetail as NutrientDetailApi } from 'types/api/product'
import { useParams } from 'react-router-dom'
import { useAlert } from 'providers/AlertProvider'
import { v4 as getRandomId } from 'uuid'
import { useProductInfo } from './Product'
import { formatLangContentField } from './utils'
import { NutrientHeader } from './NutrientHeader'
import { LanguageSelector } from './LanguageSelector'

export type NutrientHeaderType = {
  id: string
  preparationStateCode: { label: string; value: string }
  nutrientBasisQuantity: string | number
  nutrientBasisQuantityCode: { label: string; value: string }
  servingSizeDescription: {
    options: { [lang: string]: string }
    contentLanguage: string
  }
  dailyValueIntakeReference: {
    options: { [lang: string]: string }
    contentLanguage: string
  }
  mandatoryNutrientDetails: MandatoryNutrientDetails
  nutrientDetails: NutrientDetail[]
}

type NutritionalInfoFormikType = {
  nutrientHeaders: NutrientHeaderType[]
}

export const ProductNutritionalInfo = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { masterProductId } = useParams()
  const { setAlert } = useAlert()
  const { product, isLoading, error, masterProduct } = useProductInfo()
  const {
    codeListTypes: measurementUnitCodes,
    error: codeListError,
    isLoading: codeListLoading,
  } = useCodeList('measurementUnitCode', true)
  const { codeListTypes: preparationStateCodes } = useCodeList(
    'preparationStateCode',
    true
  )

  const { mutateAsync, isLoading: isLoadingPutRequest } = usePutMasterProduct(
    masterProductId as string
  )
  const initialValues = {
    nutrientHeaders: product.nutrientHeaders.map((header) => {
      return {
        id: header.id,
        nutrientBasisQuantity: header.nutrientBasisQuantity || '',
        nutrientBasisQuantityCode:
          header.nutrientBasisQuantityCode && measurementUnitCodes?.length > 0
            ? measurementUnitCodes.find(
                (code: { value: string; label: string }) =>
                  code.value === header.nutrientBasisQuantityCode
              )
            : null,
        preparationStateCode: header.preparationStateCode
          ? preparationStateCodes.find(
              (code: { value: string; label: string }) =>
                code.value === header.preparationStateCode
            )
          : null,
        servingSizeDescription: {
          options: header.servingSizeDescription,
          contentLanguage: contentLanguages[0],
        },
        dailyValueIntakeReference: {
          options: header.dailyValueIntakeReference,
          contentLanguage: contentLanguages[0],
        },
        mandatoryNutrientDetails: header.mandatoryNutrientDetails,
        nutrientDetails: header.nutrientDetails,
      }
    }),
    selectedLanguage: contentLanguages[0],
  }

  const mandatoryNutrientTemplate = (code: string) => {
    return {
      nutrientTypeCode: code,
      quantity: '',
      dailyValueIntakePercent: '',
    }
  }

  const initialValuesTemplate = {
    nutrientBasisQuantity: '',
    nutrientBasisQuantityCode: null,
    preparationStateCode: '',
    servingSizeDescription: {
      options: formatMultiLanguageField(undefined),
      contentLanguage: contentLanguages[0],
    },
    id: getRandomId(),
    dailyValueIntakeReference: {
      options: formatMultiLanguageField(undefined),
      contentLanguage: contentLanguages[0],
    },
    nutrientDetails: [],
    mandatoryNutrientDetails: {
      energy: mandatoryNutrientTemplate('ENER-'),
      fat: mandatoryNutrientTemplate('FAT'),
      saturatedFat: mandatoryNutrientTemplate('FASAT'),
      carbohydrate: mandatoryNutrientTemplate('CHO-'),
      sugar: mandatoryNutrientTemplate('SUGAR'),
      fiber: mandatoryNutrientTemplate('FIB-'),
      salt: mandatoryNutrientTemplate('NACL'),
      protein: mandatoryNutrientTemplate('PRO-'),
    },
  }
  const nutrientValidation = {
    quantity: yup.number().typeError(t('mustBeNumeric')),
    dailyValueIntakePercent: yup.number().typeError(t('mustBeNumeric')),
  }
  const validationSchema = yup.object().shape({
    nutrientHeaders: yup.array().of(
      yup.object().shape({
        nutrientBasisQuantity: yup.number().typeError(t('mustBeNumeric')),
        mandatoryNutrientDetails: yup.object().shape({
          fat: yup.object().shape(nutrientValidation),
          saturatedFat: yup.object().shape(nutrientValidation),
          carbohydrate: yup.object().shape(nutrientValidation),
          sugar: yup.object().shape(nutrientValidation),
          fiber: yup.object().shape(nutrientValidation),
          salt: yup.object().shape(nutrientValidation),
          protein: yup.object().shape(nutrientValidation),
          energy: yup.object().shape({
            kjQuantity: yup.number().typeError(t('mustBeNumeric')),
            kCalQuantity: yup.number().typeError(t('mustBeNumeric')),
            dailyValueIntakePercent: yup.number().typeError(t('mustBeNumeric')),
          }),
        }),
        nutrientDetails: yup.array().of(yup.object().shape(nutrientValidation)),
      })
    ),
  })

  const handleContentLanguageChange = (
    value: string,
    setFieldValue: (field: string, value: string) => void
  ) => {
    const fieldsToChange: string[] = []
    product.nutrientHeaders.forEach((_, i) => {
      fieldsToChange.push(
        `nutrientHeaders.${i}.servingSizeDescription`,
        `nutrientHeaders.${i}.dailyValueIntakeReference`
      )
    })
    fieldsToChange.forEach((field) => {
      setFieldValue(`${field}.contentLanguage`, value)
    })
    setFieldValue('selectedLanguage', value)
  }

  const formatNutrientDetail = (header: NutrientHeaderType) => {
    const formatMandatoryNutrients = Object.keys(
      header.mandatoryNutrientDetails
    )?.reduce((acc, key) => {
      const nutrient =
        header.mandatoryNutrientDetails[key as keyof MandatoryNutrientDetails]
      const quantityContained = []
      if (key === 'energy') {
        quantityContained.push(
          {
            $: (nutrient as Energy)?.kjQuantity
              ? Number((nutrient as Energy).kjQuantity)
              : undefined,
            '@measurementUnitCode': 'KJO',
          },
          {
            $: (nutrient as Energy)?.kCalQuantity
              ? Number((nutrient as Energy)?.kCalQuantity)
              : undefined,
            '@measurementUnitCode': 'E14',
          }
        )
      } else {
        quantityContained.push({
          $: (nutrient as NutrientDetail)?.quantity
            ? Number((nutrient as NutrientDetail)?.quantity)
            : undefined,
          '@measurementUnitCode': (nutrient as NutrientDetail)?.quantityUnit,
        })
      }
      const dailyValueIntakePercent =
        nutrient.dailyValueIntakePercent ||
        nutrient.dailyValueIntakePercent === 0
          ? Number(nutrient.dailyValueIntakePercent)
          : undefined
      const quantity = quantityContained.filter(
        (quantity) =>
          quantity.$ || quantity.$ === 0 || quantity['@measurementUnitCode']
      )
      // skip over mandatory nutrient if there are no input values
      if (!dailyValueIntakePercent && quantity.length === 0) return acc
      acc.push({
        nutrientTypeCode: nutrient.nutrientTypeCode,
        dailyValueIntakePercent,
        quantityContained,
      })
      return acc
    }, [] as NutrientDetailApi[])

    const formatOptionalNutrients = header.nutrientDetails?.reduce(
      (acc, nutrient) => {
        const quantityContained =
          acc[nutrient.nutrientTypeCode]?.quantityContained || []

        quantityContained.push({
          $: nutrient?.quantity ? Number(nutrient?.quantity) : undefined,
          '@measurementUnitCode': nutrient?.quantityUnit,
        })
        acc[nutrient.nutrientTypeCode] = {
          nutrientTypeCode: nutrient.nutrientTypeCode,
          dailyValueIntakePercent: nutrient.dailyValueIntakePercent
            ? Number(nutrient.dailyValueIntakePercent)
            : undefined,
          quantityContained,
        }
        return acc
      },
      {} as { [key: string]: NutrientDetailApi }
    )
    const optionalNutrients = formatOptionalNutrients
      ? Object.values(formatOptionalNutrients)
      : []
    return [...formatMandatoryNutrients, ...optionalNutrients]
  }

  const handleSubmit = async (values: Partial<NutritionalInfoFormikType>) => {
    const masterProductCopy = { ...masterProduct }
    const temp = masterProductCopy.tradeItem.tradeItemInformation.extensions
    const formattedNutrientHeader =
      values.nutrientHeaders?.map((header) => {
        return {
          preparationStateCode: header.preparationStateCode?.value,
          dailyValueIntakeReference: formatLangContentField(
            header.dailyValueIntakeReference.options
          ),
          nutrientBasisQuantity: {
            $: header.nutrientBasisQuantity
              ? Number(header.nutrientBasisQuantity)
              : undefined,
            '@measurementUnitCode': header.nutrientBasisQuantityCode?.value,
          },
          servingSizeDescription: formatLangContentField(
            header.servingSizeDescription.options
          ),
          nutrientDetail: formatNutrientDetail(header),
        }
      }) || []

    temp.nutritionalInformationModule = {
      ...temp.nutritionalInformationModule,
      nutrientHeader: formattedNutrientHeader,
    }

    await mutateAsync(masterProductCopy, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }

  if (isLoading || codeListLoading) return <LoadingIndicator />
  if (error || codeListError) return <h1>Error</h1>
  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values) => handleSubmit(values)}>
        {({ dirty, values, isValid }) => (
          <Form>
            {contentLanguages?.length > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: theme.spacing(2),
                  right: theme.spacing(2),
                  zIndex: 1000,
                }}>
                <LanguageSelector
                  selectedValue={values.selectedLanguage}
                  handleChange={handleContentLanguageChange}
                />
              </Box>
            )}
            <FieldArray name='nutrientHeaders'>
              {({ push }) => (
                <Box mb={8}>
                  {values.nutrientHeaders?.map((header, i) => {
                    return (
                      <Box maxWidth='500px' key={header.id}>
                        <NutrientHeader index={i} header={header} />
                      </Box>
                    )
                  })}
                  <Divider
                    sx={{
                      color: theme.palette.neutral.grey7,
                      marginTop: theme.spacing(3),
                      marginRight: theme.spacing(4),
                    }}
                  />
                  <IconButton onClick={() => push(initialValuesTemplate)}>
                    <TooltipText text={t('newGroup')}>
                      <AddIcon />
                    </TooltipText>
                  </IconButton>
                </Box>
              )}
            </FieldArray>
            <Box
              mt={3}
              sx={{
                position: 'fixed',
                bottom: isMobile ? '0px' : theme.spacing(2),
                width: isMobile ? '92%' : '504px',
                zIndex: 1000,
              }}>
              <SaveBtn
                isValid={isValid}
                dirty={dirty}
                showOverlay
                isLoadingRequest={isLoadingPutRequest}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
